import { useState, useCallback, useEffect, createContext, useContext } from 'react';

export interface AuthState {
  isAuthed: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface AuthActions {
  login: (password: string) => Promise<boolean>;
  logout: () => Promise<void>;
}

export type AuthContext = AuthState & AuthActions;

export const AuthCtx = createContext<AuthContext>({
  isAuthed: false,
  isLoading: false,
  error: null,
  login: async () => false,
  logout: async () => undefined,
});

export function useAuth(): AuthContext {
  return useContext(AuthCtx);
}

export function useAuthProvider(base = '/api/auth'): AuthContext {
  const [state, setState] = useState<AuthState>({
    isAuthed: false,
    isLoading: true,
    error: null,
  });

  // Check existing session on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/health', { credentials: 'include' });
        if (res.ok && !cancelled) {
          // If we can reach health, check if we have a valid session by trying a protected endpoint
          const check = await fetch(`${base}/check`, { credentials: 'include' });
          if (check.ok && !cancelled) {
            setState({ isAuthed: true, isLoading: false, error: null });
          }
        }
      } catch {
        // Not authed, stay on login
      } finally {
        if (!cancelled) setState(s => ({ ...s, isLoading: false }));
      }
    })();
    return () => { cancelled = true; };
  }, [base]);

  const login = useCallback(async (password: string): Promise<boolean> => {
    setState(s => ({ ...s, isLoading: true, error: null }));
    try {
      const res = await fetch(`${base}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password }),
      });

      if (res.ok) {
        setState({ isAuthed: true, isLoading: false, error: null });
        return true;
      } else {
        const data = await res.json() as { error?: string };
        setState({ isAuthed: false, isLoading: false, error: data.error ?? 'Login failed' });
        return false;
      }
    } catch (err) {
      setState({ isAuthed: false, isLoading: false, error: 'Network error' });
      return false;
    }
  }, [base]);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await fetch(`${base}/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } finally {
      setState({ isAuthed: false, isLoading: false, error: null });
    }
  }, [base]);

  return { ...state, login, logout };
}
